import React, { useState, useEffect, useCallback } from 'react';
import {
  UserCheck, UserX, Clock, CheckCircle, RefreshCw, Eye, X, ChevronUp, Copy, Building2,
} from 'lucide-react';
import { AcoesDaLinha, BotaoAcao } from '../../components/BotoesAcao';
import { aprovacaoService } from '../../services/aprovacoes';

import { useConfirm } from '../../components/ConfirmDialog';
import { authService } from '../../services/auth';
import { formatDate, formatCPF, formatPhone } from '../../utils/formatters';
import toast from 'react-hot-toast';
import { mensagemDoErro } from '../../utils/erros';
import { Paginacao } from '../../types';

/**
 * Parte do bloco `pagination` que esta tela exibe. `per_page` fica de fora
 * porque quem escolhe o tamanho da pagina e o servidor, e a interface so
 * precisa saber em que pagina esta, quantas existem e o total de registros.
 */
type EstadoDePaginacao = Pick<Paginacao, 'current_page' | 'last_page' | 'total'>;

/** Usado antes da primeira resposta e quando a API responde sem `pagination`. */
const PAGINACAO_INICIAL: EstadoDePaginacao = { current_page: 1, last_page: 1, total: 0 };

/** Rodape de paginacao das listas, no mesmo padrao visual da trilha de auditoria. */
const RodapeDePaginacao: React.FC<{
  paginacao: EstadoDePaginacao;
  resumo: string;
  onAnterior: () => void;
  onProxima: () => void;
}> = ({ paginacao, resumo, onAnterior, onProxima }) => (
  <div className="flex items-center justify-between px-4 py-3 border rounded-xl bg-gray-50">
    <span className="text-sm text-gray-600">
      {resumo} · página {paginacao.current_page} de {paginacao.last_page}
    </span>
    <div className="flex gap-2">
      <button onClick={onAnterior} disabled={paginacao.current_page <= 1}
        className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-40 hover:bg-white">Anterior</button>
      <button onClick={onProxima} disabled={paginacao.current_page >= paginacao.last_page}
        className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-40 hover:bg-white">Próxima</button>
    </div>
  </div>
);

const AprovacoesList: React.FC = () => {
  const confirm = useConfirm();
  const [tab, setTab] = useState<'pendentes' | 'historico'>('pendentes');

  // Cada aba pagina sozinha: navegar no historico nao pode mexer na posicao da
  // lista de pendentes (e vice-versa), entao pagina e paginacao sao separadas.
  const [pendentes, setPendentes] = useState<any[]>([]);
  const [paginaPendentes, setPaginaPendentes] = useState(1);
  const [paginacaoPendentes, setPaginacaoPendentes] = useState<EstadoDePaginacao>(PAGINACAO_INICIAL);
  const [carregandoPendentes, setCarregandoPendentes] = useState(true);

  const [historico, setHistorico] = useState<any[]>([]);
  const [paginaHistorico, setPaginaHistorico] = useState(1);
  const [paginacaoHistorico, setPaginacaoHistorico] = useState<EstadoDePaginacao>(PAGINACAO_INICIAL);
  const [carregandoHistorico, setCarregandoHistorico] = useState(true);

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showRejectModal, setShowRejectModal] = useState<number | null>(null);
  const [motivoRejeicao, setMotivoRejeicao] = useState('');

  const carregando = carregandoPendentes || carregandoHistorico;

  const user = authService.getUser();
  const codigoCadastro = user?.empresa?.codigo_cadastro || '';

  // A pagina chega por argumento (e nao pela dependencia do useCallback) para a
  // funcao continuar estavel: assim o efeito abaixo so dispara quando a pagina
  // ou a aba mudam, e nunca por causa de uma nova identidade de funcao.
  const buscarPendentes = useCallback(async (pagina: number) => {
    setCarregandoPendentes(true);
    try {
      const res = await aprovacaoService.getPendentes(pagina);
      if (res.success) {
        setPendentes(res.data);
        setPaginacaoPendentes(res.pagination || PAGINACAO_INICIAL);
      }
      return res;
    } catch (err: any) {
      toast.error(mensagemDoErro(err, 'Erro ao carregar os cadastros pendentes'));
      return null;
    } finally {
      setCarregandoPendentes(false);
    }
  }, []);

  const buscarHistorico = useCallback(async (pagina: number) => {
    setCarregandoHistorico(true);
    try {
      // O primeiro argumento e o filtro de status; a tela mostra o historico
      // completo (aprovados e rejeitados), por isso vai indefinido.
      const res = await aprovacaoService.getHistorico(undefined, pagina);
      if (res.success) {
        setHistorico(res.data);
        setPaginacaoHistorico(res.pagination || PAGINACAO_INICIAL);
      }
      return res;
    } catch (err: any) {
      toast.error(mensagemDoErro(err, 'Erro ao carregar o histórico de aprovações'));
      return null;
    } finally {
      setCarregandoHistorico(false);
    }
  }, []);

  // As duas listas sao buscadas mesmo com a aba escondida porque o contador de
  // cada aba (que vem de `pagination.total`) fica visivel nas duas.
  // `tab` entra como dependencia de proposito: voltar para uma aba revalida a
  // pagina em que ela estava, sem precisar do botao de recarregar.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => { buscarPendentes(paginaPendentes); }, [buscarPendentes, paginaPendentes, tab]);
  useEffect(() => { buscarHistorico(paginaHistorico); }, [buscarHistorico, paginaHistorico, tab]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const recarregarTudo = () => {
    buscarPendentes(paginaPendentes);
    buscarHistorico(paginaHistorico);
  };

  /**
   * Recarrega as listas depois de aprovar ou rejeitar um cadastro.
   *
   * O motorista sai de pendentes e entra no historico, entao as duas abas
   * mudam. Se a pagina atual de pendentes ficou vazia e nao e a primeira, ela
   * deixou de existir: recuamos uma pagina em vez de deixar o usuario olhando
   * uma lista vazia — a mudanca de estado dispara o efeito, que busca a
   * pagina anterior.
   */
  const recarregarAposDecisao = useCallback(async () => {
    const [resPendentes] = await Promise.all([
      buscarPendentes(paginaPendentes),
      buscarHistorico(paginaHistorico),
    ]);
    if (resPendentes?.success && resPendentes.data.length === 0 && paginaPendentes > 1) {
      setPaginaPendentes((atual) => atual - 1);
    }
  }, [buscarPendentes, buscarHistorico, paginaPendentes, paginaHistorico]);

  const handleAprovar = async (id: number, nome: string) => {
    if (!(await confirm({
      title: 'Aprovar motorista',
      message: `Aprovar o motorista "${nome}"? Um email de confirmação será enviado.`,
      confirmLabel: 'Aprovar',
      variant: 'primary',
    }))) return;
    try {
      const res = await aprovacaoService.aprovar(id);
      if (res.success) {
        toast.success('Motorista aprovado! Email enviado.');
        recarregarAposDecisao();
      }
    } catch (err: any) { toast.error(mensagemDoErro(err, 'Erro ao aprovar')); }
  };

  const handleRejeitar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showRejectModal || !motivoRejeicao.trim()) return;
    try {
      const res = await aprovacaoService.rejeitar(showRejectModal, motivoRejeicao);
      if (res.success) {
        toast.success('Motorista rejeitado. Email enviado.');
        setShowRejectModal(null);
        setMotivoRejeicao('');
        recarregarAposDecisao();
      }
    } catch (err: any) { toast.error(mensagemDoErro(err, 'Erro ao rejeitar')); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(codigoCadastro);
    toast.success('Código copiado!');
  };

  const renderMotoristaCard = (m: any, showActions: boolean) => (
    <div key={m.id} className="bg-white rounded-xl border">
      <div className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-900">{m.nome}</h3>
              {m.status_aprovacao === 'pendente' && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">Pendente</span>
              )}
              {m.status_aprovacao === 'aprovado' && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">Aprovado</span>
              )}
              {m.status_aprovacao === 'rejeitado' && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">Rejeitado</span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              CPF: {formatCPF(m.cpf)} | CNH: {m.cnh} ({m.categoria_cnh}) | Celular: {formatPhone(m.celular)}
            </p>
            {m.email && <p className="text-sm text-gray-500">Email: {m.email}</p>}
            <p className="text-xs text-gray-400 mt-1">Cadastro: {formatDate(m.created_at)}</p>
            {m.motivo_rejeicao && (
              <p className="text-sm text-red-600 mt-2 bg-red-50 p-2 rounded">Motivo: {m.motivo_rejeicao}</p>
            )}
          </div>
          <AcoesDaLinha>
            <BotaoAcao
              tom="azul"
              icone={expandedId === m.id ? ChevronUp : Eye}
              onClick={() => setExpandedId(expandedId === m.id ? null : m.id)}
              title="Ver dados do cadastro"
            >
              {expandedId === m.id ? 'Fechar' : 'Detalhes'}
            </BotaoAcao>
            {showActions && (
              <>
                <BotaoAcao tom="verde" icone={UserCheck} onClick={() => handleAprovar(m.id, m.nome)}>
                  Aprovar
                </BotaoAcao>
                <BotaoAcao tom="vermelho" icone={UserX}
                  onClick={() => { setShowRejectModal(m.id); setMotivoRejeicao(''); }}>
                  Rejeitar
                </BotaoAcao>
              </>
            )}
          </AcoesDaLinha>
        </div>
      </div>

      {expandedId === m.id && (
        <div className="border-t bg-gray-50 p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div><span className="text-gray-500">Nome:</span> <span className="font-medium">{m.nome}</span></div>
            <div><span className="text-gray-500">CPF:</span> <span className="font-medium">{formatCPF(m.cpf)}</span></div>
            <div><span className="text-gray-500">RG:</span> <span className="font-medium">{m.rg}</span></div>
            <div><span className="text-gray-500">Nascimento:</span> <span className="font-medium">{formatDate(m.data_nascimento)}</span></div>
            <div><span className="text-gray-500">Estado Civil:</span> <span className="font-medium capitalize">{m.estado_civil}</span></div>
            <div><span className="text-gray-500">Sexo:</span> <span className="font-medium">{m.sexo === 'M' ? 'Masculino' : 'Feminino'}</span></div>
            <div><span className="text-gray-500">Celular:</span> <span className="font-medium">{formatPhone(m.celular)}</span></div>
            <div><span className="text-gray-500">Email:</span> <span className="font-medium">{m.email || '-'}</span></div>
            <div><span className="text-gray-500">Cidade:</span> <span className="font-medium">{m.cidade}/{m.estado}</span></div>
            <div><span className="text-gray-500">Endereço:</span> <span className="font-medium">{m.endereco}, {m.numero}</span></div>
            <div><span className="text-gray-500">CNH:</span> <span className="font-medium">{m.cnh} ({m.categoria_cnh})</span></div>
            <div><span className="text-gray-500">Venc. CNH:</span> <span className="font-medium">{formatDate(m.vencimento_cnh)}</span></div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Aprovações de motoristas</h1>
          <p className="text-gray-500 mt-1">Aprovar ou rejeitar cadastros de motoristas</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={recarregarTudo}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg border">
            <RefreshCw className={`h-5 w-5 ${carregando ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Codigo de cadastro */}
      {codigoCadastro && (
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-900">Código de cadastro da empresa</p>
                <p className="text-xs text-blue-600">Compartilhe com motoristas para que se cadastrem</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <code className="px-4 py-2 bg-white rounded-lg border border-blue-200 text-xl font-mono font-bold text-blue-700 tracking-widest">
                {codigoCadastro}
              </code>
              <button onClick={copyCode} className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg" title="Copiar">
                <Copy className="h-5 w-5" />
              </button>
            </div>
          </div>
          <p className="text-xs text-blue-500 mt-2">
            Link para o motorista: {window.location.origin}/cadastro-motorista
          </p>
        </div>
      )}

      {/* Tabs — os contadores usam o total da paginacao, e nao o tamanho da
          pagina carregada, senao "Pendentes (5)" esconderia os outros 18. */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        <button onClick={() => setTab('pendentes')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === 'pendentes' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}>
          <Clock className="h-4 w-4" />
          Pendentes ({paginacaoPendentes.total})
        </button>
        <button onClick={() => setTab('historico')}
          className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            tab === 'historico' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}>
          <CheckCircle className="h-4 w-4" />
          Histórico ({paginacaoHistorico.total})
        </button>
      </div>

      {/* Lista */}
      <div className="space-y-3">
        {tab === 'pendentes' && (
          pendentes.length > 0 ? (
            <>
              {pendentes.map(m => renderMotoristaCard(m, true))}
              <RodapeDePaginacao
                paginacao={paginacaoPendentes}
                resumo={`${paginacaoPendentes.total} motorista${paginacaoPendentes.total === 1 ? '' : 's'} pendente${paginacaoPendentes.total === 1 ? '' : 's'}`}
                onAnterior={() => setPaginaPendentes((p) => Math.max(1, p - 1))}
                onProxima={() => setPaginaPendentes((p) => p + 1)}
              />
            </>
          ) : (
            <div className="text-center py-12 text-gray-400 bg-white rounded-xl border">
              <UserCheck className="h-12 w-12 mx-auto mb-3 text-green-300" />
              <p className="text-lg font-medium">Nenhum motorista pendente</p>
              <p className="text-sm mt-1">Todos os cadastros foram analisados</p>
            </div>
          )
        )}

        {tab === 'historico' && (
          historico.length > 0 ? (
            <>
              {historico.map(m => renderMotoristaCard(m, false))}
              <RodapeDePaginacao
                paginacao={paginacaoHistorico}
                resumo={`${paginacaoHistorico.total} registro${paginacaoHistorico.total === 1 ? '' : 's'} no histórico`}
                onAnterior={() => setPaginaHistorico((p) => Math.max(1, p - 1))}
                onProxima={() => setPaginaHistorico((p) => p + 1)}
              />
            </>
          ) : (
            <div className="text-center py-12 text-gray-400 bg-white rounded-xl border">
              <Clock className="h-12 w-12 mx-auto mb-3" />
              <p>Nenhum histórico de aprovações</p>
            </div>
          )
        )}
      </div>

      {/* Modal Rejeitar */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold text-red-700">Rejeitar motorista</h2>
              <button onClick={() => setShowRejectModal(null)} className="p-1 hover:bg-gray-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleRejeitar} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo da Rejeicao *</label>
                <textarea required value={motivoRejeicao} onChange={e => setMotivoRejeicao(e.target.value)}
                  rows={4} placeholder="Informe o motivo da rejeição. Ele será enviado por e-mail ao motorista."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500" />
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowRejectModal(null)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancelar</button>
                <button type="submit"
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">Confirmar Rejeicao</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AprovacoesList;
